import 'whatwg-fetch'
import browser from '@/lib/browser'
import { VERSION } from '@/lib/defines'

import { getAccessToken, removeUser } from '@/services/identity-client'
import { addBreadcrumb } from '@sentry/vue'

class ResponseError extends Error {
  response: Response

  constructor(message: string, response: Response) {
    super(message)
    this.response = response

    Object.setPrototypeOf(this, ResponseError.prototype)
  }
}

/**
 * Token used by the SpiceDB architecture to determine whether to retrieve
 * updated access information or not
 **/
export let consistencyToken: string | null = null

/**
 * *(AI-generated)*
 * Returns the current consistency token used in SpiceDB requests, or null if not set.
 */
export const getConsistencyToken = () => {
  return consistencyToken
}

/**
 * *(AI-generated)*
 * Utility functions for helping with our network request handling
 */

/** Log a network info breadcrumb to Sentry that will be included if an error is thrown
 * @param message the message to log to Sentry
 */
const logNetBreadcrumb = (message: string) => {
  addBreadcrumb({
    category: 'net',
    level: 'info',
    message,
  })
}

/**
 * Check the response status code
 *
 * @param response Response from fetch request
 * @return Resolves if status is between 200 and 300. Rejects if status is anything else.
 */
export async function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response)
  }

  // unauthenticated - clear any existing tokens and go to the appropriate login page
  if (response.status === 401) {
    await removeUser({ forUnauthorizedRequest: true })
    location.reload()
    return
  }

  if (
    response.status === 404 &&
    !browser.getLocation().pathname.includes('/purchase.html') &&
    !browser.getLocation().pathname.includes('/login') &&
    !browser.getLocation().pathname.includes('/login-purchase.html')
  ) {
    const error = new ResponseError(
      'Something went wrong. Please try again later or contact customer service.',
      response
    )

    return Promise.reject(error)
  }

  const isJson =
    response.headers.get('content-type')?.includes('application/json') ?? false
  // check if the body stream has already been read
  if (!response.bodyUsed && isJson) {
    // attempt to parse the response json
    const json = await response.json()
    return response.ok ? json : Promise.reject(json)
  }

  return Promise.reject(
    new Error(`Unsuccessful fetch request. Status: ${response.status}`)
  )
}

/**
 * Parse out the JSON from the response
 *
 * @param response Response from fetch request
 * @return JSON representation of response text
 */
export async function parseJson<T>(response: Response) {
  const isJson =
    response.headers.get('content-type')?.includes('application/json') ?? false

  if (!response.bodyUsed && isJson) {
    return (await response.json()) as T
  }

  // if there is nothing to parse, just resolve
  return Promise.resolve() as T
}

/**
 * Parse out the cursor header if it exists
 *
 * @param response Response from fetch request
 * @return cursor
 */
function getPageCursor(response: Response) {
  return response?.headers?.get('onx-page-cursor')
}

/**
 * Check for and set the onx consistency token if it exists
 *
 * @param response Response from fetch request
 */
export function checkForConsistencyToken(response: Response) {
  if (response?.headers?.has('Onx-Consistency-Token')) {
    consistencyToken = response?.headers?.get('Onx-Consistency-Token')
  }
}

/**
 * Set consistencyToken (used by Apollo client)
 *
 * @param token Consistency Token to set
 */
export function setConsistencyToken(token: string) {
  consistencyToken = token
}

/**
 * Returns the current request headers
 *
 * @param setType optional bool whether or not to set the content-type,
 * this will default to true
 * @param appOverride optional application-id override value
 * @return Request headers object
 */
export async function getHeaders(setType = true, appOverride = '') {
  const headers = new Headers({
    'ONX-Application-ID': appOverride || 'fish',
    'ONX-Application-Platform': 'web',
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    'ONX-Application-Version': VERSION!,
    'Accept-Encoding': 'gzip',
  })

  const currentToken = await getAccessToken()

  if (currentToken) {
    headers.append('Authorization', 'Bearer ' + currentToken)
  }

  if (setType) {
    headers.append('Content-Type', 'application/json')
  }

  if (consistencyToken) {
    headers.append('Onx-Consistency-Token', consistencyToken)
  }

  return headers
}

/**
 * Returns the json from the url
 *
 * @param url the get url
 * @param fetchOptions fetch options
 * @return json response
 */
export async function httpGetJson<T>(
  url: string,
  fetchOptions: RequestInit = {}
) {
  const options = {
    method: 'GET',
    headers: await getHeaders(),
    ...fetchOptions,
  }
  const fullUrl = processUrl(url)
  logNetBreadcrumb(`httpGetJson ${fullUrl}`)
  try {
    const response = await fetch(fullUrl, options)
    await checkStatus(response)
    return await parseJson<T>(response)
  } catch (e) {
    return Promise.reject(e)
  }
}

/**
 * Returns the json from the url and the page cursor
 *
 * @param url the get url
 * @param fetchOptions fetch options
 * @return json response and page cursor
 */
export async function httpGetJsonWithCursor<T>(
  url: string,
  fetchOptions: RequestInit = {}
) {
  const options = {
    method: 'GET',
    headers: await getHeaders(),
    ...fetchOptions,
  }
  const fullUrl = processUrl(url)
  logNetBreadcrumb(`httpGetJsonWithCursor ${fullUrl}`)
  try {
    const response = await fetch(processUrl(fullUrl), options)
    await checkStatus(response)
    const cursor = getPageCursor(response)
    const parsedResponse = await parseJson<T>(response)
    return {
      cursor,
      data: parsedResponse,
    }
  } catch (e) {
    return Promise.reject(e)
  }
}

/**
 * Posts payload to api and returns any response json
 *
 * @param url the post url
 * @param fetchBody body payload
 * @param fetchOptions fetch options
 * @param isJson wether or not to the body is json
 * @return json response
 */
export async function httpPostJson<T>(
  url: string,
  fetchBody: unknown | string,
  fetchOptions: RequestInit = {},
  isJson = true
) {
  const options = {
    method: 'POST',
    headers: await getHeaders(isJson),
    body: isJson ? JSON.stringify(fetchBody) : (fetchBody as string),
    ...fetchOptions,
  }
  const fullUrl = processUrl(url)
  logNetBreadcrumb(`httpPostJson ${fullUrl}`)
  try {
    const response = await fetch(fullUrl, options)
    await checkStatus(response)
    checkForConsistencyToken(response)
    return await parseJson<T>(response)
  } catch (e) {
    return Promise.reject(e)
  }
}

/**
 * Put payload to api and returns any response json
 *
 * @param url the put url
 * @param fetchBody body payload
 * @param fetchOptions fetch options
 * @return json response
 */
export async function httpPutJson<T>(
  url: string,
  fetchBody: unknown,
  fetchOptions: RequestInit = {}
) {
  const options = {
    method: 'PUT',
    headers: await getHeaders(),
    body: JSON.stringify(fetchBody),
    ...fetchOptions,
  }
  const fullUrl = processUrl(url)
  logNetBreadcrumb(`httpPutJson ${fullUrl}`)
  try {
    const response = await fetch(fullUrl, options)
    await checkStatus(response)
    checkForConsistencyToken(response)
    return await parseJson<T>(response)
  } catch (e) {
    return Promise.reject(e)
  }
}

/**
 * Patch payload to api and returns any response json
 *
 * @param url the patch url
 * @param fetchBody body payload
 * @param fetchOptions fetch options
 * @return json response
 */
export async function httpPatchJson<T>(
  url: string,
  fetchBody: unknown,
  fetchOptions: RequestInit = {}
) {
  const options = {
    method: 'PATCH',
    headers: await getHeaders(),
    body: JSON.stringify(fetchBody),
    ...fetchOptions,
  }
  const fullUrl = processUrl(url)
  logNetBreadcrumb(`httpPatchJson ${fullUrl}`)
  try {
    const response = await fetch(fullUrl, options)
    await checkStatus(response)
    checkForConsistencyToken(response)
    return await parseJson<T>(response)
  } catch (e) {
    return Promise.reject(e)
  }
}

/**
 * Delete payload to api and returns any response json
 *
 * @param url the delete url
 * @param fetchBody body payload
 * @param fetchOptions - fetch options
 * @return json response
 */
export async function httpDeleteJson<T>(
  url: string,
  fetchBody: unknown,
  fetchOptions: RequestInit = {}
) {
  const options = {
    method: 'DELETE',
    headers: await getHeaders(),
    body: JSON.stringify(fetchBody),
    ...fetchOptions,
  }
  const fullUrl = processUrl(url)
  logNetBreadcrumb(`httpDeleteJson ${fullUrl}`)
  try {
    const response = await fetch(fullUrl, options)
    await checkStatus(response)
    checkForConsistencyToken(response)
    return await parseJson<T>(response)
  } catch (e) {
    return Promise.reject(e)
  }
}

/**
 * Processes the url for a network request.  If the url starts with a valid
 * location protocol, use the url, otherwise assume it's a path and prepend
 * it with the standard api url.
 * @param url
 */
export const processUrl = (url: string) => {
  return url.startsWith('http') ? url : `${window.environment.apiUrl}${url}`
}

type ToRun = () => Promise<unknown>

/**
 * Retry a fetch request on error for a set number of tries
 *
 * @param toRun Function to run until success or until out of retries
 * @param tries Number of tries to attempt
 * @param delay Delay in milliseconds between each retry
 */
export async function retry(toRun: ToRun, tries = 1, delay = 100) {
  try {
    return await toRun()
  } catch (e) {
    // when we are down to our last try, bubble the error up
    if (tries === 1) {
      throw e
    }

    // wait the delay amount before retrying
    await _wait(delay)
    // recursively call retry
    return await retry(toRun, tries - 1, delay)
  }
}

const _wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
